import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { RadioOptionsWithConfirm, IQuestion, CardContainer, CardWithModal } from 'shared-components';

import { ApplicationState } from '../../../../../store';
import { saveVisaPrepForm } from '../../../../../store/student/actions';
import { createUpdateVPIFieldAction } from '../../../../../store/student/visaPrepForm/actions';
import QuestionPage from '../../../../templates/question';
import { VQ } from '../../../../../store/student/types';
import OtherUkVisas from './otherUkVisas';
import { IOtherUkVisas } from '../../../../../types';
import { Pages } from '../../../../../store/student/visaPrepForm/types';
import { getPageStatus } from './utils';
import { useNavigate } from 'react-router-dom';

const VisaPrepFormAddHistoryPage2 = (_props: any) => {
  // dispatch actions
  const dispatch = useDispatch();
  const onNext = () => {
    dispatch(saveVisaPrepForm(Pages.PAGE2_2));
  };
  const onChange = (fieldName: string, value: any, refValue?: any) =>
    dispatch(createUpdateVPIFieldAction(fieldName, value, refValue));

  const navigate = useNavigate();
  const onBackToTaskList = () => navigate(-1);

  // state props from store
  const hasOtherUkVisas = useSelector(({ student }: ApplicationState) => student.visa.hasOtherUkVisas, shallowEqual);
  const defaultArr = useMemo(() => {
    return [];
  }, []);
  const otherUkVisas =
    useSelector(({ student }: ApplicationState) => student.visa.otherUkVisas, shallowEqual) || defaultArr;

  // constants
  const currentStep = 2;
  const steps = 8;

  // component state
  const [complete, setComplete] = useState(false);
  const [otherUkVisasState, setOtherUkVisasState] = useState<IOtherUkVisas[]>([]);
  const [showModal, setShowModal] = useState<boolean[]>([]);

  useEffect(() => {
    setOtherUkVisasState([
      ...otherUkVisas,
      {
        visaType: '',
        issueDate: '',
        expiryDate: '',
        country: '',
        issuingAuthority: '',
      },
    ]);
    setShowModal([...otherUkVisas.map(_ => false), false]);
  }, [otherUkVisas]);

  useEffect(() => {
    const { complete: newComplete } = getPageStatus({
      hasOtherUkVisas,
      otherUkVisas,
    });
    if (newComplete !== complete) {
      setComplete(newComplete);
    }
  }, [complete, hasOtherUkVisas, otherUkVisas]);

  const toggleModal = (index: number) => {
    setShowModal(showModal.map((value, i) => (i === index ? !value : value)));
  };

  const deleteVisa = (index: number): void => {
    onChange(VQ.OTHER_UK_VISAS, [...otherUkVisas.slice(0, index), ...otherUkVisas.slice(index + 1)]);
  };

  const onHasOtherUKVisasClick = (question: IQuestion) => {
    onChange(VQ.HAS_OTHER_UK_VISAS, question.label === 'Yes');
  };

  return (
    <QuestionPage
      complete={complete}
      currentStep={currentStep}
      steps={steps}
      onNext={onNext}
      onBackToTaskList={onBackToTaskList}
      section={'Additional History'}
    >
      <RadioOptionsWithConfirm
        key={VQ.HAS_OTHER_UK_VISAS}
        id={VQ.HAS_OTHER_UK_VISAS}
        label={
          'Have you been issued any visas for UK Overseas Territories or Commonwealth Countries in the last 10 years?'
        }
        subText={'(Note: not including the UK itself)'}
        onClick={(question: any) => onHasOtherUKVisasClick(question)}
        isInline={true}
        questions={[
          {
            char: 'Y',
            label: 'Yes',
            selected: hasOtherUkVisas === true,
          },
          {
            char: 'N',
            label: 'No',
            selected: hasOtherUkVisas === false,
          },
        ]}
      />

      {hasOtherUkVisas === true && (
        <CardContainer title={`How many visas have you been issued?`}>
          {otherUkVisasState.map((visa: IOtherUkVisas, index: number) => (
            <CardWithModal
              key={index}
              id={`other-uk-visa-${index}`}
              index={index}
              modalTitle={'Add Visa'}
              cardItems={[
                {
                  label: 'Type',
                  value: visa.visaType,
                },
                {
                  label: 'Date of issue',
                  value: visa.issueDate,
                },
                {
                  label: 'Date of expiry',
                  value: visa.expiryDate,
                },
                {
                  label: 'Country / Territory',
                  value: visa.country,
                },
                {
                  label: 'Issuing authority',
                  value: visa.issuingAuthority,
                },
              ]}
              onRemove={deleteVisa}
              addButtonLabel={`Add Visa`}
              addAnotherButtonLabel={`I have another Visa to add`}
              hasAddMoreButton={
                visa.visaType === '' &&
                visa.issueDate === '' &&
                visa.expiryDate === '' &&
                visa.country === '' &&
                visa.issuingAuthority === ''
              }
              showModal={showModal[index]}
              toggleModal={toggleModal}
            >
              <OtherUkVisas
                index={index}
                visaType={visa.visaType}
                issueDate={visa.issueDate}
                expiryDate={visa.expiryDate}
                country={visa.country}
                issuingAuthority={visa.issuingAuthority}
                onSaveAndClose={(visa: IOtherUkVisas) => {
                  const visas = [...otherUkVisas];
                  visas[index] = {
                    ...visa,
                  };
                  onChange(VQ.OTHER_UK_VISAS, visas);
                  toggleModal(index);
                }}
              />
            </CardWithModal>
          ))}
        </CardContainer>
      )}
    </QuestionPage>
  );
};
export default VisaPrepFormAddHistoryPage2;
